import React, { useState, useEffect, useRef, useMemo } from "react";
import { toast } from "react-hot-toast";
import { useHistory, useParams, useLocation } from "react-router-dom";
import {
  HiOutlineTrash,
  HiOutlinePlus,
  HiChevronDown,
  HiChevronUp,
  HiInformationCircle,
  HiLink,
  HiOutlineChevronUp,
  HiOutlineChevronDown,
  HiOutlineX,
  HiOutlineDotsVertical,
  HiOutlineSparkles,
  HiOutlineRefresh,
  HiOutlineExclamation,
} from "react-icons/hi";
import { PiClockCounterClockwise } from "react-icons/pi";
import { LuCopy, LuNetwork, LuUnlink } from "react-icons/lu";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import API from "../../api";
import GroupIdLookup from "../../components/GroupIdLookup";
import SyncConflictBanner from "../../components/SyncConflictBanner";
import Trigger from "../../components/question/Trigger";
import DependencyExplorer from "../../components/dependencies/DependencyExplorer";
import { capture } from "../../instrument";
import { validateTriggerGroups, INPUT_CLASS_NAME, needsVerification, CONFIG_STATUS, CONFIG_STATUS_LABEL, guidelineText } from "../../utils";
import SyncInfoBanner from "../../components/SyncInfoBanner";
import { useHotkeys } from "react-hotkeys-hook";
import useRecordNavShortcuts from "../../hooks/useRecordNavShortcuts";
import { SHORTCUTS } from "../../utils/shortcuts";
import { notifyShortcut } from "../../utils/shortcutTip";

const AUDIENCES = [
  { audience: "secretary", label: "Secrétaire", dotClass: "bg-purple-500" },
  { audience: "patient", label: "Patient", dotClass: "bg-blue-500" },
  { audience: "ide", label: "IDE", dotClass: "bg-green-500" },
];

const GUIDELINE_TEXTAREA_CLASS =
  "w-full resize-y rounded-md border border-gray-200 bg-white px-3 py-2 text-sm leading-relaxed text-gray-900 placeholder:text-gray-300 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500";

const EditGuideline = () => {
  const history = useHistory();
  const { id } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const isDirtyRef = useRef(false);
  const markDirty = () => {
    isDirtyRef.current = true;
  };
  const [secretary, setSecretary] = useState(false);
  const [secretaryGuideline, setSecretaryGuideline] = useState("");
  const [patient, setPatient] = useState(false);
  const [patientGuideline, setPatientGuideline] = useState("");
  const [ide, setIde] = useState(false);
  const [ideGuideline, setIdeGuideline] = useState("");
  const [canShowInPreRegistration, setCanShowInPreRegistration] = useState(false);
  const [activeAudience, setActiveAudience] = useState("secretary");
  const [groupsOperator, setGroupsOperator] = useState("AND");
  const [triggerGroups, setTriggerGroups] = useState([{ operator: "AND", triggers: [{ type: "CODE_SOME", values: [], field: "" }] }]);
  const [groupId, setGroupId] = useState("");
  const [priority, setPriority] = useState(0);
  const [ubilabUrl, setUbilabUrl] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkText, setLinkText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const textareaRefs = useRef({});
  const diagramOpen = true;
  const filteredIds = location.state?.filteredIds || null;
  const currentIndex = useMemo(() => (filteredIds ? filteredIds.indexOf(id) : -1), [filteredIds, id]);
  const hasNavigation = filteredIds !== null && currentIndex !== -1;

  const preview = useMemo(() => {
    const cached = queryClient
      .getQueriesData({ queryKey: ["guidelines"] })
      .flatMap(([, guidelines]) => guidelines || [])
      .find((g) => g._id === id);
    return cached ? { text: guidelineText(cached), priority: cached.priority } : null;
  }, [id]);

  const navigateTo = (index) => {
    const targetId = filteredIds[index];
    history.replace({ pathname: `/guideline/${targetId}/edit`, state: { filteredIds } });
  };

  const navigatePrev = () => {
    if (!hasNavigation || currentIndex <= 0) return;
    if (isDirtyRef.current && !window.confirm("Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter cette page ?")) return;
    navigateTo(currentIndex - 1);
  };

  const navigateNext = () => {
    if (!hasNavigation || currentIndex >= filteredIds.length - 1) return;
    if (isDirtyRef.current && !window.confirm("Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter cette page ?")) return;
    navigateTo(currentIndex + 1);
  };

  const goBack = () => {
    if (isDirtyRef.current && !window.confirm("Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter cette page ?")) return;
    history.push("/guideline");
  };

  useRecordNavShortcuts(
    {
      onPrev: navigatePrev,
      onNext: navigateNext,
      onBack: goBack,
      canPrev: hasNavigation && currentIndex > 0,
      canNext: hasNavigation && currentIndex < filteredIds?.length - 1,
      backEnabled: !showLinkModal,
    },
    [hasNavigation, currentIndex, filteredIds, showLinkModal],
  );

  const {
    data: guidelineData,
    isLoading: isLoadingData,
    isError,
  } = useQuery({
    queryKey: ["guideline", id],
    queryFn: async () => {
      const response = await API.get(`/guideline/${id}`);
      if (!response.ok) throw new Error(response.message || "Impossible de charger la recommandation");
      return response.data;
    },
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!guidelineData) return;
    setSecretary(!!guidelineData.secretary);
    setSecretaryGuideline(guidelineData.secretaryGuideline || "");
    setPatient(!!guidelineData.patient);
    setPatientGuideline(guidelineData.patientGuideline || "");
    setIde(!!guidelineData.ide);
    setIdeGuideline(guidelineData.ideGuideline || "");
    setCanShowInPreRegistration(!!guidelineData.canShowInPreRegistration);
    setGroupsOperator(guidelineData.groupsOperator);
    setTriggerGroups(guidelineData.triggerGroups);
    setGroupId(guidelineData.groupId || "");
    setPriority(guidelineData.priority || 0);
    setUbilabUrl(guidelineData.ubilabUrl || "");
    setShowAdvanced(!!guidelineData.groupId || guidelineData.priority > 0);
    isDirtyRef.current = false;
  }, [guidelineData]);

  useEffect(() => {
    if (!isError) return;
    toast.error("Une erreur est survenue lors du chargement de la recommandation");
    history.push("/guideline");
  }, [isError]);

  const { data: relatedGuidelines = [] } = useQuery({
    queryKey: ["guidelines", "byGroup", groupId],
    queryFn: async () => {
      const response = await API.get(`/guideline?groupId=${groupId}`);
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
    enabled: !!groupId,
  });

  const { data: diagramData } = useQuery({
    queryKey: ["diagramData"],
    queryFn: async () => {
      const [questions, guidelines, actions] = await Promise.all([API.get("/question"), API.get("/guideline"), API.get("/action")]);
      if (!questions.ok || !guidelines.ok || !actions.ok) throw new Error("Failed to load diagram data");
      return { questions: questions.data, guidelines: guidelines.data, actions: actions.data };
    },
    enabled: diagramOpen,
  });

  const enabledAudiences = AUDIENCES.filter(({ audience }) => (audience === "secretary" ? secretary : audience === "patient" ? patient : ide));

  const audienceText = (audience) => (audience === "secretary" ? secretaryGuideline : audience === "patient" ? patientGuideline : ideGuideline);
  const setAudienceText = (audience, value) => {
    if (audience === "secretary") setSecretaryGuideline(value);
    else if (audience === "patient") setPatientGuideline(value);
    else setIdeGuideline(value);
  };
  const setAudienceEnabled = (audience, enabled) => {
    if (audience === "secretary") setSecretary(enabled);
    else if (audience === "patient") setPatient(enabled);
    else setIde(enabled);
  };

  const onAudienceAdd = (audience) => {
    setAudienceEnabled(audience, true);
    if (enabledAudiences.length > 0) setAudienceText(audience, audienceText(enabledAudiences[0].audience));
    setActiveAudience(audience);
    markDirty();
  };

  const onAudienceRemove = (audience) => {
    setAudienceEnabled(audience, false);
    setAudienceText(audience, "");
    if (audience === "patient") setCanShowInPreRegistration(false);
    markDirty();
  };

  const insertLinkAtCursor = (text, url) => {
    const textarea = textareaRefs.current[activeAudience];
    const markdownLink = `[${text}](${url})`;
    markDirty();
    if (!textarea) {
      setAudienceText(activeAudience, audienceText(activeAudience) + markdownLink);
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentText = audienceText(activeAudience);
    const newGuidelineText = currentText.substring(0, start) + markdownLink + currentText.substring(end);
    setAudienceText(activeAudience, newGuidelineText);

    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + markdownLink.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const handleInsertLink = () => {
    if (!linkText.trim() || !linkUrl.trim()) {
      toast.error("Veuillez remplir le texte et l'URL du lien");
      return;
    }

    insertLinkAtCursor(linkText.trim(), linkUrl.trim());
    setShowLinkModal(false);
    setLinkText("");
    setLinkUrl("");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const { data: sharingNetwork } = useQuery({
    queryKey: ["network-config"],
    queryFn: async () => {
      const response = await API.get("/network-config");
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
    staleTime: Infinity,
  });
  const isNetworkReplica = !!guidelineData?.networkSourceId;
  const isNetworkOwner = !!guidelineData?.sharedToNetwork;

  const updateMutation = useMutation({
    mutationFn: (body) => API.put(`/guideline/${id}`, body),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message);
      isDirtyRef.current = false;
      toast.success("Recommandation mise à jour avec succès");
      queryClient.invalidateQueries({ queryKey: ["guidelines"] });
      queryClient.invalidateQueries({ queryKey: ["guideline", id] });
      queryClient.invalidateQueries({ queryKey: ["diagramData"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskCount"] });
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const { data: syncOverrides = [] } = useQuery({
    queryKey: ["sync-overrides", id],
    enabled: Boolean(id),
    queryFn: async () => {
      const response = await API.get(`/sync/override?docId=${id}`);
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
  });
  const hasConflict = syncOverrides.some((override) => override.conflict);

  const verifyMutation = useMutation({
    mutationFn: () => API.post("/guideline/bulk", { ids: [id], action: "verify" }),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message);
      toast.success("Recommandation vérifiée");
      queryClient.invalidateQueries({ queryKey: ["guidelines"] });
      queryClient.invalidateQueries({ queryKey: ["guideline", id] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskCount"] });
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const restoreMutation = useMutation({
    mutationFn: () => API.put(`/guideline/${id}`, { isDeleted: false }),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message);
      toast.success("Recommandation restaurée");
      queryClient.invalidateQueries({ queryKey: ["guidelines"] });
      queryClient.invalidateQueries({ queryKey: ["guideline", id] });
      queryClient.invalidateQueries({ queryKey: ["diagramData"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskCount"] });
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => API.delete(`/guideline/${id}`),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message);
      toast.success("Recommandation supprimée avec succès");
      queryClient.invalidateQueries({ queryKey: ["guidelines"] });
      queryClient.invalidateQueries({ queryKey: ["diagramData"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskCount"] });
      history.push("/guideline");
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const shareMutation = useMutation({
    mutationFn: () => API.post("/network-config/guideline", { fromIds: [id] }),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message);
      if (response.data?.errors?.length) return toast.error(response.data.errors[0].message);
      toast.success("Recommandation partagée au réseau");
      queryClient.invalidateQueries({ queryKey: ["guidelines"] });
      queryClient.invalidateQueries({ queryKey: ["guideline", id] });
      queryClient.invalidateQueries({ queryKey: ["diagramData"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskCount"] });
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const unshareMutation = useMutation({
    mutationFn: () => API.delete(`/network-config/guideline/${id}`),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message);
      toast.success("Recommandation retirée du réseau");
      queryClient.invalidateQueries({ queryKey: ["guidelines"] });
      queryClient.invalidateQueries({ queryKey: ["guideline", id] });
      queryClient.invalidateQueries({ queryKey: ["diagramData"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["taskCount"] });
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const isMutating = updateMutation.isPending || deleteMutation.isPending;
  const isDeleted = !!guidelineData?.isDeleted;

  const updateGuideline = () => {
    if (!enabledAudiences.length) return toast.error("Veuillez ajouter au moins une audience");
    if (enabledAudiences.some(({ audience }) => !audienceText(audience).trim())) return toast.error("Veuillez remplir tous les champs obligatoires");
    if (!triggerGroups?.length) return toast.error("Veuillez ajouter au moins un groupe de conditions");

    const error = validateTriggerGroups(triggerGroups);
    if (error) return toast.error(error);

    updateMutation.mutate({
      secretary,
      secretaryGuideline,
      patient,
      patientGuideline,
      ide,
      ideGuideline,
      canShowInPreRegistration,
      groupsOperator,
      triggerGroups,
      groupId: groupId.trim() || null,
      priority: Number(priority) || 0,
      ubilabUrl: ubilabUrl.trim() || null,
    });
  };

  useHotkeys(SHORTCUTS.EDIT_SAVE.keys, () => updateGuideline(), { preventDefault: true, enableOnFormTags: true, enabled: !isMutating && !isDeleted }, [
    updateGuideline,
    isMutating,
    isDeleted,
  ]);

  const duplicateGuideline = () => {
    const data = {
      secretary,
      secretaryGuideline: secretaryGuideline && `${secretaryGuideline} (copie)`,
      patient,
      patientGuideline: patientGuideline && `${patientGuideline} (copie)`,
      ide,
      ideGuideline: ideGuideline && `${ideGuideline} (copie)`,
      canShowInPreRegistration,
      groupsOperator,
      triggerGroups: JSON.parse(JSON.stringify(triggerGroups)),
      groupId,
      priority,
      ubilabUrl,
    };
    history.push({ pathname: "/guideline/add", state: { duplicate: data } });
  };

  const deleteGuideline = () => {
    const confirm = window.confirm("Êtes-vous sûr de vouloir supprimer cette recommandation ?");
    if (!confirm) return;
    deleteMutation.mutate();
  };

  return (
    <div className="flex h-[calc(100vh-60px)] flex-col bg-gray-50">
      {hasNavigation && (
        <div className="flex items-center gap-1.5 border-b border-gray-200 bg-white px-3 py-1.5">
          <button type="button" className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600" onClick={goBack} title="Fermer (Esc)">
            <HiOutlineX className="h-4 w-4" />
          </button>
          <div className="mx-0.5 h-4 w-px bg-gray-200" />
          <button
            type="button"
            className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-500"
            onClick={navigatePrev}
            disabled={currentIndex <= 0}
          >
            <HiOutlineChevronUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-500"
            onClick={navigateNext}
            disabled={currentIndex >= filteredIds.length - 1}
          >
            <HiOutlineChevronDown className="h-4 w-4" />
          </button>
          <span className="ml-1 text-xs tabular-nums text-gray-500">
            {currentIndex + 1} / {filteredIds.length}
          </span>
        </div>
      )}

      {isDeleted && (
        <div className="flex items-center gap-3 border-b border-gray-200 bg-gray-100 px-8 py-2.5">
          <HiOutlineTrash className="h-4 w-4 shrink-0 text-gray-500" />
          <p className="text-sm text-gray-600">Cette recommandation est supprimée : elle est affichée en lecture seule. Restaurez-la pour pouvoir la modifier.</p>
        </div>
      )}

      <div className="flex items-start justify-between border-b border-gray-200 bg-white px-8 pb-5 pt-6">
        <div className="flex min-w-0 items-center gap-3">
          <h1 className="truncate text-lg font-semibold text-gray-900">{guidelineText({ secretaryGuideline, patientGuideline, ideGuideline }) || preview?.text || "Recommandation"}</h1>
          {(isNetworkOwner || isNetworkReplica) && (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-primary-200 bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700">
              <LuNetwork className="h-3 w-3" />
              {isNetworkOwner ? "Réseau · propriétaire" : "Réseau"}
            </span>
          )}
          {guidelineData && needsVerification(guidelineData) && (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-orange-200 bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700">
              {guidelineData.configStatus === CONFIG_STATUS.CREATED ? <HiOutlineSparkles className="h-3 w-3" /> : <HiOutlineRefresh className="h-3 w-3" />}
              {CONFIG_STATUS_LABEL[guidelineData.configStatus]}
            </span>
          )}
          {hasConflict && (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
              <HiOutlineExclamation className="h-3 w-3" />
              Conflit
            </span>
          )}
          {isDeleted && (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-gray-300 bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
              <HiOutlineTrash className="h-3 w-3" />
              Supprimée
            </span>
          )}
          {!isDeleted && guidelineData?._sources?.some((s) => s.missingFromSource) && (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
              <HiOutlineTrash className="h-3 w-3" />
              Supprimé
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1.5 pl-4">
          <div className="relative" ref={menuRef}>
            <button type="button" className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              <HiOutlineDotsVertical className="h-5 w-5" />
            </button>
            {isMenuOpen && (
              <div className="absolute right-0 z-10 mt-1 w-48 rounded-lg border border-gray-200 bg-white shadow-lg">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-t-lg px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                  onClick={() => {
                    setIsMenuOpen(false);
                    window.open(`/audit-log/guidelines/${id}`, "_blank", "noopener,noreferrer,width=1000,height=800");
                  }}
                >
                  <PiClockCounterClockwise className="h-4 w-4" />
                  Historique
                </button>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                  onClick={() => {
                    setIsMenuOpen(false);
                    duplicateGuideline();
                  }}
                >
                  <LuCopy className="h-4 w-4" />
                  Dupliquer
                </button>
                {!isDeleted && sharingNetwork && !isNetworkOwner && !isNetworkReplica && (
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                    onClick={() => {
                      setIsMenuOpen(false);
                      if (isDirtyRef.current) return toast.error("Enregistrez vos modifications avant de partager");
                      if (
                        !window.confirm(
                          `Partager cette recommandation au réseau ${sharingNetwork.name} ? Ce site en reste propriétaire : ses modifications et les mises à jour Ubilab s'appliqueront aux autres sites.`,
                        )
                      )
                        return;
                      shareMutation.mutate();
                    }}
                    disabled={shareMutation.isPending}
                  >
                    <LuNetwork className="h-4 w-4" />
                    Partager au réseau
                  </button>
                )}
                {!isDeleted && isNetworkOwner && (
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                    onClick={() => {
                      setIsMenuOpen(false);
                      if (isDirtyRef.current) return toast.error("Enregistrez vos modifications avant de dé-partager");
                      if (
                        !window.confirm(
                          "Ne plus partager cette recommandation ? Elle redevient locale à ce site ; les autres sites la verront comme retirée du réseau et décideront de la conserver ou la supprimer.",
                        )
                      )
                        return;
                      unshareMutation.mutate();
                    }}
                    disabled={unshareMutation.isPending}
                  >
                    <LuUnlink className="h-4 w-4" />
                    Ne plus partager
                  </button>
                )}
                {!isDeleted && (
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded-b-lg px-3 py-2 text-sm text-red-600 transition-colors hover:bg-red-50"
                    onClick={() => {
                      setIsMenuOpen(false);
                      deleteGuideline();
                    }}
                    disabled={isMutating}
                  >
                    <HiOutlineTrash className="h-4 w-4" />
                    Supprimer
                  </button>
                )}
              </div>
            )}
          </div>
          {!isDeleted && guidelineData && needsVerification(guidelineData) && (
            <button
              type="button"
              className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
              onClick={() => {
                if (isDirtyRef.current) return toast.error("Enregistrez vos modifications avant de vérifier");
                verifyMutation.mutate();
              }}
              disabled={verifyMutation.isPending}
            >
              {verifyMutation.isPending ? "Vérification..." : "Marquer vérifié"}
            </button>
          )}
          {isDeleted ? (
            <button
              type="button"
              className="rounded-lg bg-primary-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
              onClick={() => restoreMutation.mutate()}
              disabled={restoreMutation.isPending}
            >
              {restoreMutation.isPending ? "Restauration..." : "Restaurer"}
            </button>
          ) : (
            <button
              type="button"
              className="rounded-lg bg-primary-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
              onClick={() => {
                updateGuideline();
                notifyShortcut("EDIT_SAVE");
              }}
              disabled={isMutating || isLoadingData}
            >
              {updateMutation.isPending ? "Enregistrement..." : "Enregistrer"}
            </button>
          )}
        </div>
      </div>

      {isLoadingData ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-primary-500" />
          <span className="text-sm text-gray-400">Chargement...</span>
        </div>
      ) : (
        <div className="grid flex-1 grid-cols-5 overflow-hidden">
          <div className={`col-span-3 flex-1 overflow-y-auto ${isDeleted ? "pointer-events-none opacity-60" : ""}`} onChangeCapture={markDirty}>
            <fieldset disabled={isDeleted} className="mx-auto max-w-4xl px-8 py-6">
              {isNetworkOwner && (
                <div className="mb-6 flex items-start gap-3 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
                  <LuNetwork className="mt-0.5 h-4 w-4 shrink-0 text-primary-500" />
                  <p className="text-sm text-primary-700">
                    Recommandation partagée au réseau {sharingNetwork?.name || ""} — ce site est propriétaire : vos modifications et les mises à jour Ubilab s'appliquent aux{" "}
                    {sharingNetwork?.indexNames?.length || ""} sites du réseau.
                  </p>
                </div>
              )}
              {isNetworkReplica && (
                <div className="mb-6 flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                  <LuNetwork className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
                  <p className="text-sm text-gray-600">
                    Élément du réseau {sharingNetwork?.name || ""} — géré par un autre site. Vos modifications restent locales et seront signalées en conflit si le site
                    propriétaire modifie les mêmes champs.
                  </p>
                </div>
              )}
              <SyncConflictBanner docId={id} />
              <h3 className="mb-4 text-lg font-medium text-gray-900">Détails</h3>

              <div className="divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white">
                <div className="flex items-center gap-4 px-5 py-3.5">
                  <label className="w-44 shrink-0 text-[13px] font-medium text-gray-600">URL Ubilab</label>
                  <input type="url" className={INPUT_CLASS_NAME + " flex-1"} placeholder="https://..." value={ubilabUrl} onChange={(e) => setUbilabUrl(e.target.value)} />
                </div>
              </div>

              <div className="mt-6 space-y-3">
                <div className="flex items-baseline justify-between px-1">
                  <h4 className="text-sm font-medium text-gray-700">Versions par audience</h4>
                  <p className="text-xs text-gray-400">Markdown supporté · liens via [texte](url)</p>
                </div>

                {enabledAudiences.map(({ audience, label, dotClass }) => {
                  return (
                    <div key={audience} className="overflow-hidden rounded-lg border border-gray-100 bg-white">
                      <div className="flex items-center gap-2.5 border-b border-gray-100 bg-gray-50/40 px-5 py-2.5">
                        <span className={`h-2 w-2 shrink-0 rounded-full ${dotClass}`} />
                        <span className="text-[13px] font-medium text-gray-800">{label}</span>
                        {audience === "patient" && (
                          <label className="ml-2 flex cursor-pointer items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700">
                            <input
                              type="checkbox"
                              className="h-3.5 w-3.5 cursor-pointer rounded border-gray-300 text-primary-500 focus:ring-1 focus:ring-primary-500"
                              checked={canShowInPreRegistration}
                              onChange={(e) => setCanShowInPreRegistration(e.target.checked)}
                            />
                            Affichable en pré-enregistrement
                          </label>
                        )}
                        <div className="ml-auto flex items-center gap-1">
                          <button
                            type="button"
                            className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-gray-500 transition-colors hover:bg-primary-50 hover:text-primary-700"
                            onClick={() => {
                              setActiveAudience(audience);
                              setShowLinkModal(true);
                            }}
                            title="Insérer un lien Markdown"
                          >
                            <HiLink className="h-3.5 w-3.5" />
                            Lien
                          </button>
                          <button
                            type="button"
                            onClick={() => onAudienceRemove(audience)}
                            className="rounded p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
                            title={`Supprimer la version ${label}`}
                          >
                            <HiOutlineX className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      <div className="px-5 py-4">
                        <textarea
                          ref={(el) => {
                            textareaRefs.current[audience] = el;
                          }}
                          className={GUIDELINE_TEXTAREA_CLASS}
                          rows={5}
                          placeholder="Saisissez votre recommandation ici…"
                          value={audienceText(audience)}
                          onChange={(e) => setAudienceText(audience, e.target.value)}
                          onFocus={() => setActiveAudience(audience)}
                        />
                      </div>
                    </div>
                  );
                })}

                {enabledAudiences.length < AUDIENCES.length && (
                  <div className="flex flex-wrap items-center gap-2 px-1 pt-1">
                    <span className="text-xs text-gray-400">Ajouter une version :</span>
                    {AUDIENCES.filter((candidate) => !enabledAudiences.includes(candidate)).map(({ audience, label, dotClass }) => (
                      <button
                        key={audience}
                        type="button"
                        onClick={() => onAudienceAdd(audience)}
                        className="flex items-center gap-1.5 rounded-md border border-dashed border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-primary-400 hover:bg-primary-50 hover:text-primary-700"
                      >
                        <span className={`h-1.5 w-1.5 rounded-full ${dotClass}`} />
                        <HiOutlinePlus className="h-3.5 w-3.5" />
                        {label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <h3 className="mb-4 mt-8 text-lg font-medium text-gray-900">Conditions</h3>

              <div className="rounded-lg border border-gray-100 bg-white p-4">
                <Trigger
                  groupsOperator={groupsOperator}
                  setGroupsOperator={(v) => {
                    markDirty();
                    setGroupsOperator(v);
                  }}
                  triggerGroups={triggerGroups}
                  setTriggerGroups={(v) => {
                    markDirty();
                    setTriggerGroups(v);
                  }}
                  codesIncludeRelated
                />
              </div>

              <div className="mt-6 border-t border-gray-100 pt-4">
                <button type="button" className="flex items-center text-sm font-medium text-primary-600 hover:text-primary-800" onClick={() => setShowAdvanced(!showAdvanced)}>
                  {showAdvanced ? (
                    <>
                      <HiChevronUp className="mr-1" />
                      Masquer les options avancées
                    </>
                  ) : (
                    <>
                      <HiChevronDown className="mr-1" />
                      Afficher les options avancées
                    </>
                  )}
                </button>

                {showAdvanced && (
                  <div className="mt-4 divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white">
                    <div className="flex items-start gap-4 px-5 py-3.5">
                      <HiInformationCircle className="mt-0.5 h-4 w-4 shrink-0 text-primary-500" />
                      <p className="text-xs text-gray-600">Ces options permettent de gérer des recommandations similaires qui doivent être affichées selon une priorité.</p>
                    </div>
                    <div className="flex items-center gap-4 px-5 py-3.5">
                      <label className="w-44 shrink-0 text-[13px] font-medium text-gray-600">Groupe d'alternatives</label>
                      <div className="flex-1" data-track="guideline-group-alternatives">
                        <GroupIdLookup
                          onChange={(v) => {
                            markDirty();
                            setGroupId(v);
                          }}
                          initialValue={groupId}
                          placeholder="Identifiant du groupe (ex: bilan_sanguin)"
                          currentId={id}
                        />
                      </div>
                    </div>
                    {groupId && (
                      <div className="flex items-center gap-4 px-5 py-3.5">
                        <label className="w-44 shrink-0 text-[13px] font-medium text-gray-600">Priorité</label>
                        <input
                          type="number"
                          className={INPUT_CLASS_NAME}
                          value={priority}
                          onChange={(e) => setPriority(Number(e.target.value) >= 0 ? Number(e.target.value) : 0)}
                        />
                      </div>
                    )}

                    {groupId && relatedGuidelines.length > 0 && (
                      <div className="px-5 py-3.5">
                        <div className="rounded-lg border border-primary-100 bg-primary-50 p-2">
                          <div className="mb-1 text-xs text-primary-800">
                            {relatedGuidelines.length} recommandation{relatedGuidelines.length > 1 ? "s" : ""} dans ce groupe
                          </div>
                          <div className="max-h-32 space-y-1 overflow-y-auto">
                            {relatedGuidelines.map((g) => (
                              <div
                                key={g._id}
                                className="flex cursor-pointer items-center justify-between rounded border border-gray-200 bg-white p-1.5 text-xs hover:bg-primary-50"
                                onClick={() => window.open(`/guideline/${g._id}/edit`, "_blank")}
                              >
                                <div className="mr-2 flex-1 truncate">{guidelineText(g)}</div>
                                <div className="text-gray-500">{g.priority}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </fieldset>
          </div>

          <div className="col-span-2 shrink-0 overflow-y-auto border-l border-gray-200 bg-white p-6">
            {guidelineData?._sources?.length > 0 && (
              <div className="mb-6">
                <h3 className="mb-4 text-lg font-medium text-gray-900">Synchronisation</h3>
                <SyncInfoBanner sources={guidelineData?._sources} docId={id} entityType="guideline" lastSyncChangedFields={guidelineData?.lastSyncChangedFields} lastSyncChangedAt={guidelineData?.lastSyncChangedAt} />
              </div>
            )}
            <h3 className="mb-4 text-lg font-medium text-gray-900">Dépendances</h3>
            {diagramData ? (
              <DependencyExplorer
                data={{
                  questions: diagramData.questions,
                  guidelines: [
                    ...diagramData.guidelines.filter((g) => g._id !== id),
                    { _id: id, secretary, secretaryGuideline, patient, patientGuideline, ide, ideGuideline, canShowInPreRegistration, groupsOperator, triggerGroups, groupId, priority },
                  ],
                  actions: diagramData.actions,
                }}
                currentGuidelineId={id}
                className="rounded-lg border border-gray-100"
              />
            ) : (
              <div className="flex items-center justify-center rounded-lg border border-gray-100 py-8 text-sm text-gray-400">Chargement du diagramme...</div>
            )}
          </div>
        </div>
      )}

      {showLinkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={() => setShowLinkModal(false)}>
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-lg" onClick={(e) => e.stopPropagation()}>
            <h3 className="mb-4 text-lg font-semibold text-gray-800">Insérer un lien</h3>
            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Texte du lien</label>
                <input
                  type="text"
                  className="w-full rounded-lg border border-gray-300 p-2 outline-none transition-all focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
                  placeholder="Ex: En savoir plus"
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleInsertLink();
                    }
                  }}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">URL</label>
                <input
                  type="url"
                  className="w-full rounded-lg border border-gray-300 p-2 outline-none transition-all focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
                  placeholder="https://exemple.com"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleInsertLink();
                    }
                  }}
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                className="rounded-lg bg-gray-200 px-4 py-2 text-gray-800 transition-colors hover:bg-gray-300"
                onClick={() => {
                  setShowLinkModal(false);
                  setLinkText("");
                  setLinkUrl("");
                }}
              >
                Annuler
              </button>
              <button type="button" className="rounded-lg bg-primary-500 px-4 py-2 text-white transition-colors hover:bg-primary-700" onClick={handleInsertLink}>
                Insérer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditGuideline;
